<template>
  <div id="category-panel" class="h-100">
    <div class="search-bar">
      <h3 class="section-title">Categories</h3>
      <input type="text" name="searchCategories" id="searchCategories" class="form-control-sm search-field"
             placeholder="Search categories" v-model="categorySearchInput">
    </div>
    <div class="category-grid" ref="categoryGrid" v-on:scroll="handleScroll">
      <button class="category" @click="fastLineOrBack">
        <span class="category-name">{{ category ? 'Back' : 'Fast Line' }}</span>
      </button>

      <button class="category" v-for="category in filteredCategories" @click="selectCategory(category.name)">
        <span class="category-name">{{ category.name }}</span>
      </button>
    </div>
    <div class="scroll-controls" v-if="isScrollable">
      <button class="scroll-control" @click="scrollToTop">
        <font-awesome-icon :icon="['fas', 'chevron-up']" />
      </button>
      <button class="scroll-control" @click="scrollToBottom">
        <font-awesome-icon :icon="['fas', 'chevron-down']" />
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ProductCategories',
  components: {},
  props: {
    showMainCategories: {
      type: Function,
      required: false
    }
  },
  computed: {
    categorySearch() {
      return this.$store.getters.getSearchCategories
    },
    categories() {
      return this.$store.getters.getCategories
    },
    selectedCategories() {
      if (this.category && this.subCategory) {
        let main = this.categories
            .find(category => category.name.localeCompare(this.category, undefined, {sensitivity: 'base'}) === 0)
        let sub = main.children
            .find(category => category.name.localeCompare(this.subCategory, undefined, {sensitivity: 'base'}) === 0)
        if (!sub || sub.length === 0) {
          return main.children
        }
        return sub.children || []
      } else if (this.category) {
        return this.categories
            .find(category => category.name.localeCompare(this.category, undefined, {sensitivity: 'base'}) === 0)
            .children
      } else {
        return this.categories
      }
    },
    filteredCategories() {
      if (!this.categorySearchInput || this.categorySearchInput.trim() === '') {
        return this.selectedCategories;
      }
      const searchTerm = this.categorySearchInput.toLowerCase();
      return this.selectedCategories.filter(cat => 
        cat.name.toLowerCase().includes(searchTerm)
      );
    },
  },
  data() {
    return {
      category: undefined,
      subCategory: undefined,
      subSubCategory: undefined,
      isScrollable: false,
      scrollPosition: 0,
      maxScrollTop: 0,
      categorySearchInput: ''
    }
  },
  mounted() {
    this.searchProducts()
  },
  methods: {
    clearCategory() {
      this.category = undefined
      this.subCategory = undefined
      this.subSubCategory = undefined
      this.searchProducts()
    },
    selectMainCategory() {
      this.$store.commit(`setLimit`, 30)
      this.subSubCategory = undefined
      this.subCategory = undefined
      this.searchProducts()
    },
    selectSubCategory() {
      this.$store.commit(`setLimit`, 30)
      this.subCategory = undefined
      this.searchProducts()
    },
    selectCategory(category) {
      this.$store.commit(`setLimit`, 30)
      if (this.category && this.subCategory) {
        this.subSubCategory = category
      } else if (this.category) {
        this.subCategory = category
      } else {
        this.category = category
      }

      this.showCategories()
      this.searchProducts()
    },
    showCategories() {
      let show = this.showMainCategories
      if (show) {
        show(false)
      }
    },
    searchProducts() {
      if (this.category) {
        let searchQuery = {
          category: this.category || '',
          subCategory: this.subCategory || '',
          sub2Category: this.subSubCategory || '',
        };
        this.$store.commit(`setSearchCategories`, searchQuery)
        this.$store.dispatch('searchStock', searchQuery)
      } else {
        this.$store.dispatch('getFastLine')
      }
    },
    fastLineOrBack() {
      if (this.category && this.subCategory && this.subSubCategory) {
        this.subSubCategory = undefined
      } else if (this.category && this.subCategory) {
        this.subCategory = undefined
      } else {
        this.category = undefined
      }
      this.showCategories()
      this.searchProducts()
    },
    scrollToTop() {
      const newScrollTop = Math.max(this.scrollPosition - 110, 0);
      this.$refs.categoryGrid.scrollTo({
        top: newScrollTop,
        behavior: 'smooth',
      });
    },
    scrollToBottom() {
      const newScrollTop = Math.min(this.scrollPosition + 110, this.maxScrollTop);
      this.$refs.categoryGrid.scrollTo({
        top: newScrollTop,
        behavior: 'smooth',
      });
    },
    handleScroll() {
      const scroll = {
        scrollTop: this.$refs.categoryGrid.scrollTop,
        scrollHeight: this.$refs.categoryGrid.scrollHeight,
        clientHeight: this.$refs.categoryGrid.clientHeight
      }
      this.scrollPosition = scroll.scrollTop
      this.maxScrollTop = scroll.scrollHeight - scroll.clientHeight
      this.isScrollable = scroll.scrollHeight > scroll.clientHeight;
    },
  },
}
</script>

<style lang="scss" scoped>
#category-panel {
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
  height: 100%;

  .search-bar {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 16px;
    padding: 16px;
    background: linear-gradient(135deg, rgba(var(--accent-primary-rgb), 0.04) 0%, rgba(var(--accent-primary-rgb), 0.02) 100%);
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);

    .section-title {
      margin: 0;
      font-size: 18px;
      font-weight: 700;
      color: var(--text-primary);
      text-transform: uppercase;
      letter-spacing: 0.5px;
      white-space: nowrap;
      min-width: fit-content;
    }

    .search-field {
      width: 100%;
      border: 2px solid rgba(0, 0, 0, 0.08);
      border-radius: 12px;
      background-color: var(--card-bg);
      color: var(--text-primary);
      padding: 12px 16px;
      font-size: 15px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.04);
      
      &:focus {
        border-color: var(--accent-primary);
        box-shadow: 0 0 0 4px rgba(var(--accent-primary-rgb), 0.15), 0 4px 12px rgba(0, 0, 0, 0.08);
        outline: none;
        transform: translateY(-1px);
      }
      
      &::placeholder {
        color: rgba(var(--text-primary-rgb), 0.5);
      }
    }
  }

  .scroll-controls {
    position: absolute;
    bottom: 16px;
    right: 16px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    z-index: 10;

    .scroll-control {
      width: 64px;
      height: 64px;
      border-radius: 50%;
      background: var(--accent-primary);
      color: var(--accent-primary-contrast);
      border: none;
      box-shadow: 0 4px 12px rgba(var(--accent-primary-rgb), 0.35);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 22px;

      &:hover {
        transform: scale(1.15);
        background: var(--accent-primary-hover, var(--accent-primary));
        box-shadow: 0 6px 24px rgba(var(--accent-primary-rgb), 0.5);
      }

      &:active {
        transform: scale(0.95);
      }
    }
  }

  .title {
    font-size: 1.5rem;
    margin: 0 0 10px 0;
    text-align: center;
  }

  .category-breadcrumbs {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    .clear-btn {
      color: var(--text-primary);
      background: transparent;
      width: 30px;
      height: 30px;
      transition: var(--transition-default);
      
      &:hover {
        color: var(--accent-primary);
      }
    }
  }

  .category-grid {
    overflow-y: auto;
    overflow-x: hidden;
    display: grid;
    gap: 10px;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: minmax(70px, auto);
    padding: 16px;
    height: 100%;
    
    // Custom scrollbar
    &::-webkit-scrollbar {
      width: 8px;
    }
    
    &::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.05);
      border-radius: 4px;
    }
    
    &::-webkit-scrollbar-thumb {
      background: rgba(var(--accent-primary-rgb), 0.3);
      border-radius: 4px;
      
      &:hover {
        background: rgba(var(--accent-primary-rgb), 0.5);
      }
    }

    .category {
      min-height: 70px;
      width: 100%;
      background: linear-gradient(135deg, var(--card-bg) 0%, rgba(var(--accent-primary-rgb), 0.02) 100%);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      padding: 12px;
      box-sizing: border-box;
      position: relative;
      border: 2px solid transparent;
      border-radius: 12px;
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
      cursor: pointer;
      overflow: hidden;
      
      // Hover effect overlay
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(135deg, rgba(var(--accent-primary-rgb), 0.1) 0%, rgba(var(--accent-primary-rgb), 0.05) 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
      }

      &:hover {
        border-color: rgba(var(--accent-primary-rgb), 0.3);
        transform: translateY(-4px);
        box-shadow: 0 8px 20px rgba(var(--accent-primary-rgb), 0.15);
        
        &::before {
          opacity: 1;
        }
        
        .category-name {
          color: var(--accent-primary);
        }
      }
      
      &:active {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(var(--accent-primary-rgb), 0.2);
      }

      .category-name {
        font-size: 14px;
        font-weight: 600;
        color: var(--text-primary);
        word-wrap: break-word;
        text-align: center;
        line-height: 1.3;
        max-width: 100%;
        overflow: hidden;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        position: relative;
        z-index: 1;
        transition: color 0.3s ease;
      }
    }
  }

  /* Responsive styling */
  @media (max-width: 1200px) {
    .category-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      padding: 12px;
    }
  }

  @media (max-width: 768px) {
    .category-grid {
      grid-template-columns: repeat(2, 1fr);
      gap: 8px;
      padding: 10px;
    }

    .scroll-controls {
      .scroll-control {
        width: 56px;
        height: 56px;
        font-size: 20px;
      }
    }
  }

  @media (max-width: 480px) {
    .category-grid {
      grid-template-columns: 1fr;
      gap: 8px;
      padding: 8px;
    }

    .title {
      font-size: 1.2rem;
    }

    .category {
      min-height: 60px;

      .category-name {
        font-size: 13px;
      }
    }

    .scroll-controls {
      .scroll-control {
        width: 48px;
        height: 48px;
        font-size: 18px;
      }
    }
  }
}
</style>
